import { Component, Inject, OnInit } from '@angular/core'
import { CommonModule } from '@angular/common'
import {
    FormArray,
    FormBuilder,
    FormControl,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms'
import { MatCardModule } from '@angular/material/card'
import { MatTableModule } from '@angular/material/table'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { DatePipe } from '@angular/common'
import {
    affectationKind,
    affectationScope,
    supportKind,
    remaningSupport,
} from './tracking.definitions'
import { MatDatepickerModule } from '@angular/material/datepicker'
import {
    DateAdapter,
    MatNativeDateModule,
    MatOption,
} from '@angular/material/core'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { TrackingListComponent } from '../tracking-list/tracking-list.component'
import {
    MAT_DIALOG_DATA,
    MatDialog,
    MatDialogRef,
} from '@angular/material/dialog'
import { MatSelect } from '@angular/material/select'
import { TrackingService } from '../../../services/tracking/tracking.service'
import { Tracking } from '../../../models/tracking.interface'
import { ToastrService } from 'ngx-toastr'
import { Title } from '@angular/platform-browser'
import { SelectFileComponent } from '../../select-file/select-file.component'
import { UploadfileService } from '../../../services/uploadfile/uploadfile.service'
import { PersonService } from '../../../services/person/persona.service'
import { Outsider } from '../../../models/outsider.interface'
import { SearchSelectComponent } from '../../../components/search-select/search-select.component'
import { LoadingService } from '../../../services/loadingService/loading.service'

@Component({
    selector: 'app-tracking-form',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatCardModule,
        MatFormFieldModule,
        MatInputModule,
        MatTableModule,
        MatButtonModule,
        MatIconModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatOption,
        MatSelect,
        SearchSelectComponent,
    ],
    templateUrl: './tracking-form.component.html',
    styleUrl: './tracking-form.component.css',
    providers: [DatePipe],
})
export class TrackingFormComponent implements OnInit {
    selectedFile: File | null = null
    selectedFileName: string = ''
    affectationKind = affectationKind
    affectationScope = affectationScope
    supportKind = supportKind
    remaningSupport = remaningSupport
    selectedTracking: Tracking | null = null
    trackingId: string | null = null
    outsiders: Outsider[] = []

    constructor(
        private fb: FormBuilder,
        private dialogRef: MatDialogRef<TrackingListComponent>,
        private trackingService: TrackingService,
        private title: Title,
        private toastr: ToastrService,
        private datePipe: DatePipe,
        private dialog: MatDialog,
        private uploadfileService: UploadfileService,
        private personService: PersonService,
        private loadingService: LoadingService,
        private adapter: DateAdapter<Date>,
        @Inject(MAT_DIALOG_DATA) public data: any
    ) {
        this.title.setTitle('Seguimientos'), this.adapter.setLocale('es-CO')
    }

    ngOnInit(): void {
        if (!this.selectedTracking) {
            const hoy = new Date()
            this.trackingForm.patchValue({ date: hoy })
        }

        this.trackingService.selectedTrackingSubject$.subscribe((tracking) => {
            if (tracking) {
                this.trackingId = String(tracking.id)
                this.selectedTracking = tracking
                this.trackingForm.patchValue({
                    ...this.selectedTracking,
                    date: this.trackingService.toLocalDateOnly(tracking.date),
                })
                this.victims.clear()
                if (Array.isArray(tracking.victims)) {
                    tracking.victims.forEach((victim: Outsider) => {
                        this.victims.push(this.fb.control(victim))
                    })
                }

                this.perpetrors.clear()
                if (Array.isArray(tracking.perpetrors)) {
                    tracking.perpetrors.forEach((perp: Outsider) => {
                        this.perpetrors.push(this.fb.control(perp))
                    })
                }
            }
        })
    }

    searchPerson = (value: string) => {
        return this.personService.searchPerson(value)
    }
    showPerson = (person: Outsider) =>
        `${person.identificationCode} - ${person.name} ${person.lastName}`
    personIdentifier = (person: Outsider) => person.id

    trackingForm = this.fb.group({
        date: [null as Date | null],
        affectationKind: ['', Validators.required],
        affectationScope: ['', Validators.required],
        affectationDetail: ['', Validators.required],
        commitments: ['', Validators.required],
        supportKind: ['', Validators.required],
        supportDetail: ['', Validators.required],
        remainingSupport: ['', Validators.required],
        victims: this.fb.array([]),
        perpetrors: this.fb.array([]),
    })

    get victims(): FormArray {
        return this.trackingForm.get('victims') as FormArray
    }

    get perpetrors(): FormArray {
        return this.trackingForm.get('perpetrors') as FormArray
    }

    addVictim() {
        this.victims.push(this.fb.control(null))
    }

    removeVictim(index: number) {
        this.victims.removeAt(index)
    }

    addPerpetror() {
        this.perpetrors.push(this.fb.control(null))
    }

    removePerpetror(index: number) {
        this.perpetrors.removeAt(index)
    }

    openUploadModal(): void {
        const dialogRef = this.dialog.open(SelectFileComponent, {
            width: 'auto',
            height: 'auto',
            maxHeight: '80vh',
        })

        dialogRef.afterClosed().subscribe((file: File | null | undefined) => {
            if (file instanceof File) {
                this.selectedFile = file
            }
            if (file === null) {
                this.clearSelectedFile()
            }
        })
    }

    clearSelectedFile(): void {
        this.selectedFile = null
    }

    closeDialog() {
        this.dialogRef.close()
    }

    saveTraking() {
        if (this.trackingForm.invalid) {
            this.toastr.error('Faltan campos por llenar.')
            this.trackingForm.markAllAsTouched()
            return
        }

        const formValue = this.trackingForm.getRawValue() as {
            date: Date
            affectationKind: string
            affectationScope: string
            affectationDetail: string
            commitments: string
            supportKind: string
            supportDetail: string
            remainingSupport: string
            victims: Outsider[]
            perpetrors: Outsider[]
        }

        const formattedDate = this.datePipe.transform(
            formValue.date,
            'yyyy-MM-dd'
        )

        // JSON sin archivo
        const payload = {
            ...formValue,
            date: formattedDate,
            incident: this.data.incidentId,
            victims: formValue.victims
                ?.filter((victim) => !!victim)
                .map((victim) => victim.id),
            perpetrors: formValue.perpetrors
                ?.filter((perpetror) => !!perpetror)
                .map((perpetror) => perpetror.id),
        }

        // FormData
        const formData = new FormData()
        formData.append('payload', JSON.stringify(payload))

        // Archivo
        if (this.selectedFile) {
            formData.append('file', this.selectedFile, this.selectedFile.name)
        }
        this.loadingService.show(true)
        const request$ = !this.selectedTracking
            ? this.trackingService.createTracking(formData)
            : this.trackingService.updateTracking(
                  this.selectedTracking.id,
                  formData
              )

        request$.subscribe({
            next: () => {
                this.loadingService.show(false)
                this.toastr.success(
                    !this.selectedTracking
                        ? 'CREADO CORRECTAMENTE!'
                        : 'ACTUALIZADO CORRECTAMENTE!',
                    'Exitoso!',
                    { progressBar: true, timeOut: 5000 }
                )
                this.dialogRef.close(true)
            },
            error: (err) => {
                this.loadingService.show(false)
                this.toastr.error('No se pudo guardar!', 'Error!', {
                    progressBar: true,
                    timeOut: 5000,
                })
            },
        })
    }

    openDocumentPdf(): void {
        const id = this.selectedTracking?.fileAttach?.fileUrl
        const name =
            this.selectedTracking?.fileAttach?.fileName || 'archivo.pdf'
        if (!id) return
        this.uploadfileService.openPdfById(id, name).subscribe({
            error: () => {
                const msg = `No se logro descargar o previsualizar el archivo`
                this.toastr.error(msg, 'Error', {
                    progressBar: true,
                    timeOut: 5000,
                })
            },
        })
    }

    openSelectedPdf(): void {
        if (!this.selectedFile) return
        if (this.selectedFile.type !== 'application/pdf') {
            const msg = `Solo podemos mostrarte archivos PDF`
            this.toastr.warning(msg, 'Aviso')
            return
        }
        this.uploadfileService.openLocalPdf(
            this.selectedFile,
            this.selectedFile.name
        )
    }

    hasFile(tracking: Tracking) {
        return (
            !!tracking?.fileAttach?.fileUrl && !!tracking?.fileAttach?.fileName
        )
    }
}
